import axios from 'axios';

import { JoinInfo, TutorInvitationInfo } from './models/Organization';
import type { OrganizationPublicSummary } from './models/OrganizationPublicSummary';
import { SpaceSearchResult, SpaceSummary } from './models/Space';
import { RoomSummary } from './models/Room';
import { CalendarEvent } from './models/CalendarEvent';
import { SpaceGear } from './models/SpaceGear';
import { AdditionalGear } from './models/AdditionalGear';
import { Review } from './models/CreateReviewRequest';
import {
  PassOfferDetail,
  PassOfferSummary,
  PassTargetDetail,
  PassTargetType,
} from './models/Pass';
import { SubscriptionPlanSummary } from './models/Subscription';
import { applyClientChannelHeader } from './utils/clientChannel';
import { applyAllowTestOrgsHeader } from './utils/experimentalFeatures';

export const HOST_RETENTION_EVENTS = {
  APP_DOWNLOAD_CLICKED: 'HOST_APP_DOWNLOAD_CLICKED',
  APP_OPENED: 'HOST_APP_OPENED',
  PUBLIC_SCHOOL_LINK_OPENED: 'HOST_PUBLIC_SCHOOL_LINK_OPENED',
  PUBLIC_SCHOOL_LINK_COPIED: 'HOST_PUBLIC_SCHOOL_LINK_COPIED',
  TUTOR_BOOKING_CREATED: 'HOST_TUTOR_BOOKING_CREATED',
  TUTOR_OFFER_SAVED: 'HOST_TUTOR_OFFER_SAVED',
  CALENDAR_EVENT_CREATED: 'HOST_CALENDAR_EVENT_CREATED',
} as const;

export type HostRetentionEvent =
  (typeof HOST_RETENTION_EVENTS)[keyof typeof HOST_RETENTION_EVENTS];

function currentActorMetadata(): Record<string, string> {
  const assumedUserId = localStorage.getItem('assumed_user_id');
  const userId = assumedUserId || localStorage.getItem('user_id');
  const metadata: Record<string, string> = {};

  if (userId) {
    metadata.actorUserId = userId;
    metadata.userId = userId;
  }
  const assumedUserName = localStorage.getItem('assumed_user_name');
  if (assumedUserName) {
    metadata.userName = assumedUserName;
  }
  const phoneNumber = localStorage.getItem('phone_number');
  if (phoneNumber) {
    metadata.userPhoneNumber = phoneNumber;
  }

  return metadata;
}

// Define the base API URL based on the environment
const baseURL = process.env.REACT_APP_API_BASE_URL + "/public";

// Create a class to encapsulate the API client logic
class PublicApiClient {
  public axiosInstance;

  // Mock database
  private mockUsers: any[] = JSON.parse(localStorage.getItem('mockUsers') || '[]');
  private pendingVerifications: Record<string, string> = {}; // phone -> code

  constructor() {
    // Axios instance with base URL and other configurations
    this.axiosInstance = axios.create({
      baseURL,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });

    // Only send Bearer on endpoints that return user-specific fields (e.g. bookingAccess).
    // Sending a stale token on /spaces/search caused 401s and broke search for logged-in users.
    this.axiosInstance.interceptors.request.use(
      (config) => {
        applyClientChannelHeader(config.headers);
        applyAllowTestOrgsHeader(config.headers);
        const accessToken = localStorage.getItem('access_token');
        if (!accessToken) return config;
        const rel = typeof config.url === 'string' ? config.url.split('?')[0] : '';
        const method = (config.method || 'get').toLowerCase();
        const needsUserContext = method === 'get' && rel.startsWith('/rooms/');
        if (needsUserContext) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  /** Space name / text search is applied on the frontend after this call. */
  public async searchSpaces(
    city: string,
    roomAvailableFromTime?: string,
    durationInMinutes?: number,
    gearTypes?: string[],
    roomCategories?: string[]
  ): Promise<SpaceSearchResult[]> {
    try {
      const params: any = {
        city,
      };
      
      if (roomAvailableFromTime) {
        params.roomAvailableFromTime = roomAvailableFromTime;
      }
      if (durationInMinutes) {
        params.durationInMinutes = durationInMinutes;
      }
      if (gearTypes && gearTypes.length > 0) {
        params.gearTypes = gearTypes.join(',');
      }
      if (roomCategories && roomCategories.length > 0) {
        // Send as array for Spring Boot to parse correctly
        params.roomCategories = roomCategories;
      }
      
      console.log('Search params:', params); // Debug log
      
      const response = await this.axiosInstance.get<SpaceSearchResult[]>('/spaces/search', {
        params,
        paramsSerializer: {
          indexes: null, // This formats array params as: roomCategories=REHEARSAL&roomCategories=RECORDING
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error searching spaces:', error);
      throw error;
    }
  }

  public async getAllSpaces(roomAvailableFromTime?: string, durationInMinutes?: number, gearTypes?: string[]): Promise<SpaceSearchResult[]> {
    try {
      const params: any = {};
      
      if (roomAvailableFromTime) {
        params.roomAvailableFromTime = roomAvailableFromTime;
      }
      if (durationInMinutes) {
        params.durationInMinutes = durationInMinutes;
      }
      if (gearTypes && gearTypes.length > 0) {
        params.gearTypes = gearTypes.join(',');
      }
      
      const response = await this.axiosInstance.get<SpaceSearchResult[]>('/spaces', {
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching all spaces:', error);
      throw error;
    }
  }

  public async getSpaceSummary(spaceId: string): Promise<SpaceSummary> {
    try {
      const response = await this.axiosInstance.get<SpaceSummary>(`/spaces/${spaceId}`);
      console.debug('[PublicApiClient] getSpaceSummary response', {
        endpoint: `${baseURL}/spaces/${spaceId}`,
        spaceId,
        hasDiscountSettings: Boolean((response.data as any)?.discountSettings),
        discountSettings: (response.data as any)?.discountSettings ?? null,
        topLevelKeys: response.data ? Object.keys(response.data as any) : [],
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching space summary for space ${spaceId}:`, error);
      throw error;
    }
  }

  public async getOrganizationPublicSummary(organizationId: string): Promise<OrganizationPublicSummary> {
    try {
      const response = await this.axiosInstance.get<OrganizationPublicSummary>(
        `/organizations/${organizationId}`,
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching organization summary for organization ${organizationId}:`, error);
      throw error;
    }
  }

  public async getRoomSummary(id: string): Promise<RoomSummary> {
    try {
      const response = await this.axiosInstance.get<RoomSummary>(`/rooms/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching room with id ${id}:`, error);
      throw error;
    }
  }

  public async getRoomCalendarEvents(id: string, fromTime: string, toTime: string): Promise<CalendarEvent[]> {
    try {
      const response = await this.axiosInstance.get<CalendarEvent[]>(`/rooms/${id}/calendar-events`, {
        params: {
          fromTime,
          toTime,
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching calendar events for room ${id}:`, error);
      throw error;
    }
  }

  public async getAvailableSpaceGears(
    spaceId: string,
    availableFromTime: string,
    durationInMinutes: number
  ): Promise<SpaceGear[]> {
    try {
      const response = await this.axiosInstance.get<SpaceGear[]>(`/spaces/${spaceId}/gears`, {
        params: {
          availableFromTime,
          durationInMinutes,
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching available gears for space ${spaceId}:`, error);
      throw error;
    }
  }

  public async getAvailableRoomGears(
    roomId: string,
    availableFromTime: string,
    durationInMinutes: number
  ): Promise<AdditionalGear[]> {
    try {
      const response = await this.axiosInstance.get<AdditionalGear[]>(`/rooms/${roomId}/gears`, {
        params: {
          availableFromTime,
          durationInMinutes,
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching available gears for room ${roomId}:`, error);
      throw error;
    }
  }

  public async getJoinInfo(joinCode: string): Promise<JoinInfo> {
    try {
      const response = await this.axiosInstance.get<JoinInfo>(`/join-info/${joinCode}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching join info with join code ${joinCode}:`, error);
      throw error;
    }
  }

  public async getTutorInvitationInfo(token: string): Promise<TutorInvitationInfo> {
    try {
      const response = await this.axiosInstance.get<TutorInvitationInfo>(
        `/tutor-invitation-info/${token}`,
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching tutor invitation info for token ${token}:`, error);
      throw error;
    }
  }

  public async getCities(): Promise<string[]> {
    try {
      const response = await this.axiosInstance.get<string[]>('/locations');
      return response.data;
    } catch (error) {
      console.error('Error fetching cities:', error);
      throw error;
    }
  }

  public async getReviews(spaceId: string): Promise<Review[]> {
    try {
      const response = await this.axiosInstance.get<Review[]>(`/reviews/${spaceId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching reviews for space ${spaceId}:`, error);
      throw error;
    }
  }

  public async trackEvent(eventType: string, metadata?: Record<string, any>): Promise<void> {
    try {
      await this.axiosInstance.post('/analytics/events', {
        eventType,
        metadata: {
          source: 'frontend',
          ...currentActorMetadata(),
          ...metadata,
        },
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      // Silently fail tracking to not disrupt user experience
      console.warn('Error tracking event:', error);
    }
  }

  public async trackHostRetentionEvent(
    eventType: HostRetentionEvent,
    metadata?: Record<string, any>
  ): Promise<void> {
    await this.trackEvent(eventType, metadata);
  }

  /**
   * GET /v1/public/passes — active pass offers, optionally filtered by target type / ids.
   * Items include embedded `targets` (same shape as /passes/targets).
   */
  public async listPassOffers(params?: {
    targetType?: PassTargetType;
    targetIds?: string[];
  }): Promise<PassOfferSummary[]> {
    try {
      const query: Record<string, string | string[]> = {};
      if (params?.targetType) {
        query.targetType = params.targetType;
      }
      if (params?.targetIds && params.targetIds.length > 0) {
        query.targetIds = params.targetIds;
      }
      const response = await this.axiosInstance.get<PassOfferSummary[]>('/passes', {
        params: query,
        paramsSerializer: { indexes: null },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching pass offers:', error);
      throw error;
    }
  }

  /**
   * GET /v1/public/passes/targets — resolve human-readable metadata for pass target ids.
   * Prefer embedded `targets` on public offer summaries when available.
   */
  public async listPassTargets(params: {
    targetType: PassTargetType;
    targetIds: string[];
  }): Promise<PassTargetDetail[]> {
    const uniqueIds = Array.from(new Set(params.targetIds.filter(Boolean)));
    if (uniqueIds.length === 0) {
      return [];
    }
    try {
      const response = await this.axiosInstance.get<PassTargetDetail[]>('/passes/targets', {
        params: {
          targetType: params.targetType,
          targetIds: uniqueIds,
        },
        paramsSerializer: { indexes: null },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching pass targets:', error);
      throw error;
    }
  }

  /** GET /v1/public/passes/{passId} */
  public async getPassOffer(passId: string): Promise<PassOfferDetail> {
    try {
      const response = await this.axiosInstance.get<PassOfferDetail>(`/passes/${passId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching pass offer ${passId}:`, error);
      throw error;
    }
  }

  /** GET /v1/public/subscription/plans — signup catalogue (no HOST/org required). */
  public async getSubscriptionPlans(): Promise<SubscriptionPlanSummary[]> {
    try {
      const response = await this.axiosInstance.get<SubscriptionPlanSummary[]>('/subscription/plans');
      return response.data;
    } catch (error) {
      console.error('Error fetching public subscription plans:', error);
      throw error;
    }
  }
}

export default PublicApiClient;

export const apiClient = new PublicApiClient();
